// Audio service for handling audio playback and analysis
import WaveSurfer from 'wavesurfer.js';
import { generateWaveformData, normalizeWaveform } from '@utils/audio';

class AudioService {
  private audioContext: AudioContext | null = null;
  private wavesurfers: Map<string, WaveSurfer> = new Map();

  constructor() {
    if (typeof window !== 'undefined') {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  async loadAudioBuffer(url: string): Promise<AudioBuffer> {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioContext = this.getAudioContext();
    return audioContext.decodeAudioData(arrayBuffer);
  }

  createWaveSurfer(container: HTMLElement, options?: Partial<any>): WaveSurfer {
    const wavesurfer = WaveSurfer.create({
      container,
      waveColor: '#30d870',
      progressColor: '#00ff61',
      cursorColor: '#ffffff',
      barWidth: 2,
      barRadius: 3,
      height: 80,
      normalize: true,
      ...options,
    });

    return wavesurfer;
  }

  registerWaveSurfer(id: string, wavesurfer: WaveSurfer): void {
    this.wavesurfers.set(id, wavesurfer);
  }

  getWaveSurfer(id: string): WaveSurfer | undefined {
    return this.wavesurfers.get(id);
  }

  destroyWaveSurfer(id: string): void {
    const wavesurfer = this.wavesurfers.get(id);
    if (wavesurfer) {
      wavesurfer.destroy();
      this.wavesurfers.delete(id);
    }
  }

  async analyzeAudio(file: File): Promise<{
    duration: number;
    waveform: number[];
    bpm?: number;
    key?: string;
  }> {
    const arrayBuffer = await file.arrayBuffer();
    const audioContext = this.getAudioContext();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const waveformData = generateWaveformData(audioBuffer, 1000);
    const normalizedWaveform = normalizeWaveform(waveformData);

    return {
      duration: audioBuffer.duration,
      waveform: normalizedWaveform,
      // BPM and key detection would be implemented here
    };
  }

  playAudio(url: string): HTMLAudioElement {
    const audio = new Audio(url);
    audio.play();
    return audio;
  }

  pauseAudio(audio: HTMLAudioElement): void {
    audio.pause();
  }

  stopAudio(audio: HTMLAudioElement): void {
    audio.pause();
    audio.currentTime = 0;
  }

  setVolume(audio: HTMLAudioElement, volume: number): void {
    audio.volume = Math.max(0, Math.min(1, volume));
  }

  cleanup(): void {
    this.wavesurfers.forEach((wavesurfer) => wavesurfer.destroy());
    this.wavesurfers.clear();
    
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}

export const audioService = new AudioService();
export default audioService;




